import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    MenuItem,
    Stack,
    TextField,
    Typography
} from '@mui/material';

import {TokenValidator} from '../../../../../utils';
import {Orders} from '../../../../../controllers';

const pairKey = (client, type) => `${String(client || '').trim().toLowerCase().replace(/\s+/g, '')}::${String(type || '').trim().toLowerCase().replace(/\s+/g, '')}`;
const fgsText = value => value ? `FGS-${String(value).replace(/^FGS-/i, '')}` : 'Unavailable';

export default function EditOrderInfoDialog({open, onClose, order, onUpdated}) {
    const [loading, setLoading] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const [data, setData] = React.useState(null);
    const [form, setForm] = React.useState({case_client: '', case_type: '', reason: ''});
    const [review, setReview] = React.useState(false);

    React.useEffect(() => {
        if (!open || !order) return;
        let active = true;
        const load = async() => {
            setLoading(true);
            setError('');
            setData(null);
            setReview(false);
            const token = await TokenValidator.GetInternalUser();
            const response = await Orders.GetEditableInfo(order.id, token);
            if (!active) return;
            setLoading(false);
            if (!response || !response.success) {
                setError(response ? response.message : 'Unable to load editable Order Info');
                return;
            }
            setData(response.result);
            setForm({
                case_client: response.result.current.case_client,
                case_type: response.result.current.case_type,
                reason: ''
            });
        };
        load();
        return () => { active = false; };
    }, [open, order]);

    const clients = React.useMemo(() => {
        const values = data ? data.allowedPairs.map(pair => pair.client) : [];
        if (data && data.current.case_client) values.push(data.current.case_client);
        return [...new Set(values)].sort((a, b) => a.localeCompare(b));
    }, [data]);

    const typePairs = React.useMemo(() => {
        if (!data) return [];
        return data.allowedPairs.filter(pair => pairKey(pair.client, '') === pairKey(form.case_client, ''));
    }, [data, form.case_client]);

    const types = React.useMemo(() => {
        const values = typePairs.map(pair => pair.type);
        if (data && pairKey(form.case_client, '') === pairKey(data.current.case_client, '')) values.push(data.current.case_type);
        return [...new Set(values)].sort((a, b) => a.localeCompare(b));
    }, [typePairs, data, form.case_client]);

    const selectedPair = React.useMemo(() => {
        if (!data) return null;
        const key = pairKey(form.case_client, form.case_type);
        return data.allowedPairs.find(pair => pairKey(pair.client, pair.type) === key) || null;
    }, [data, form.case_client, form.case_type]);

    const changed = Boolean(data) && (
        form.case_client !== data.current.case_client || form.case_type !== data.current.case_type
    );
    const reasonValid = form.reason.trim().length >= 3 && form.reason.trim().length <= 500;
    const blocked = Boolean(data && data.eligibility.blockers.length);

    const changeClient = event => {
        const client = event.target.value;
        const available = data.allowedPairs.filter(pair => pairKey(pair.client, '') === pairKey(client, ''));
        const typeStillValid = available.some(pair => pairKey(pair.client, pair.type) === pairKey(client, form.case_type));
        setForm({...form, case_client: client, case_type: typeStillValid ? form.case_type : (available[0] ? available[0].type : '')});
        setReview(false);
    };

    const changeType = event => {
        setForm({...form, case_type: event.target.value});
        setReview(false);
    };

    const close = () => {
        if (busy) return;
        onClose();
    };

    const confirm = async() => {
        if (!data || !selectedPair || !changed || !reasonValid || blocked) return;
        setBusy(true);
        setError('');
        const changes = {};
        if (form.case_client !== data.current.case_client) changes.case_client = form.case_client;
        if (form.case_type !== data.current.case_type) changes.case_type = form.case_type;
        const token = await TokenValidator.GetInternalUser();
        const response = await Orders.UpdateEditableInfo(order.id, {
            changes,
            expected: {
                case_client: data.current.case_client,
                case_type: data.current.case_type
            },
            reason: form.reason.trim()
        }, token);
        setBusy(false);
        if (!response || !response.success) {
            setError(response ? response.message : 'Unable to update Order Info');
            setReview(false);
            return;
        }
        onClose();
        await onUpdated();
        alert('Order Info updated successfully.');
    };

    return (
        <Dialog open={open} onClose={close} maxWidth='sm' fullWidth>
            <DialogTitle>{review ? 'Review Changes' : 'Edit Order Info'}</DialogTitle>
            <Divider />
            <DialogContent>
                {loading && <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress /></Box>}
                {error && <Alert severity='error' sx={{mb: 2}}>{error}</Alert>}
                {!loading && data && !review && (
                    <Stack gap={2} sx={{pt: 1}}>
                        <TextField label='Order ID' value={order.id} InputProps={{readOnly: true}} />
                        <TextField select label='Order Client' value={form.case_client} onChange={changeClient} disabled={busy || blocked}>
                            {clients.map(client => <MenuItem key={client} value={client}>{client}</MenuItem>)}
                        </TextField>
                        <TextField select label='Order Type' value={form.case_type} onChange={changeType} disabled={busy || blocked}>
                            {types.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                        </TextField>
                        <TextField label='Order FGS' value={fgsText(selectedPair && selectedPair.fgs)} InputProps={{readOnly: true}}
                            helperText='Assigned automatically from Order Client + Order Type' />
                        <TextField label='ZPL Template' value={selectedPair ? selectedPair.template : 'Unavailable'} InputProps={{readOnly: true}} />
                        <TextField required multiline minRows={2} label='Reason for change' value={form.reason}
                            inputProps={{maxLength: 500}} onChange={event => setForm({...form, reason: event.target.value})}
                            error={form.reason.length > 0 && !reasonValid}
                            helperText='Required, 3 to 500 characters' />
                        {data.eligibility.blockers.map(item => <Alert key={item} severity='error'>{item}</Alert>)}
                        {data.eligibility.warnings.map(item => <Alert key={item} severity='warning'>{item}</Alert>)}
                        {data.eligibility.partTypes.length > 0 && (
                            <Alert severity='info'>Existing part types: {data.eligibility.partTypes.map(item => `${item.type} (${item.count})`).join(', ')}</Alert>
                        )}
                    </Stack>
                )}
                {!loading && data && review && (
                    <Stack gap={2} sx={{pt: 1}}>
                        <Typography variant='subtitle1'>Only the following Order Info values will change:</Typography>
                        {form.case_client !== data.current.case_client && <Typography><strong>Client:</strong> {data.current.case_client} → {form.case_client}</Typography>}
                        {form.case_type !== data.current.case_type && <Typography><strong>Type:</strong> {data.current.case_type} → {form.case_type}</Typography>}
                        <Typography><strong>Derived FGS:</strong> {fgsText(data.current.fgs)} → {fgsText(selectedPair && selectedPair.fgs)}</Typography>
                        <Typography><strong>ZPL:</strong> {data.current.template || 'Unavailable'} → {selectedPair ? selectedPair.template : 'Unavailable'}</Typography>
                        <Typography><strong>Reason:</strong> {form.reason.trim()}</Typography>
                        <Alert severity='warning'>Existing parts and stored labels will not be rewritten. Future prints and Manual Reprint will use the new Client/Type configuration.</Alert>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={review ? () => setReview(false) : close} disabled={busy}>{review ? 'Back' : 'Cancel'}</Button>
                {!review && <Button variant='contained' onClick={() => setReview(true)}
                    disabled={loading || busy || blocked || !changed || !selectedPair || !reasonValid}>Review Changes</Button>}
                {review && <Button variant='contained' onClick={confirm} disabled={busy}>{busy ? <CircularProgress size={22} /> : 'Confirm Update'}</Button>}
            </DialogActions>
        </Dialog>
    );
}
